import { useEffect } from 'react'
import { create } from 'zustand'

//	Detects that a newer frontend build has been deployed while this page was open, so the
//	onboarding banner can recommend a reload (see OnboardingStep.ReloadForUpdate).
//
//	Every build stamps its id twice: into the bundle as __BUILD_ID__, and into /version.json next to
//	index.html (both from the build-id plugin in vite.config.ts). The page periodically fetches
//	/version.json from its own origin and compares. The file comes from the same deploy that a
//	reload would load, so a mismatch always means "reloading gets you something newer" -- which a
//	build id reported by the chat server could not promise, since the backend is deployed
//	separately and can run a different commit than the frontend.
//
//	The prompt is not dismissible: an old bundle may no longer work against the current backend, so
//	it stays until the user reloads (or a rollback makes the deployed id match the page's again).
//	A reload does get the new bundle: sw.js passes every fetch through to the network uncached.

export const currentBuildId: string = __BUILD_ID__

const VERSION_URL = '/version.json'
const CHECK_INTERVAL_MS = 5 * 60 * 1000

interface AppUpdateState {
	//	the build id most recently read from /version.json, when it differs from currentBuildId
	availableBuildId: string | null
	setAvailableBuildId: (buildId: string | null) => void
}

export const useAppUpdateStore = create<AppUpdateState>((set, get) => ({
	availableBuildId: null,
	setAvailableBuildId: (buildId) => {
		if (get().availableBuildId === buildId) return
		set({ availableBuildId: buildId })
	},
}))

export function isUpdatePromptVisible(state: Pick<AppUpdateState, 'availableBuildId'>): boolean {
	return state.availableBuildId !== null
}

//	Returns the deployed build id, or null when it cannot be determined. Never throws: a missing
//	file is served as index.html by the SPA fallback, so a non-JSON body is an expected outcome.
export async function fetchDeployedBuildId(fetchFn: typeof fetch = fetch): Promise<string | null> {
	try {
		const response = await fetchFn(VERSION_URL, { cache: 'no-store' })
		if (!response.ok) return null
		const body = await response.json()
		return typeof body?.buildId === 'string' && body.buildId !== '' ? body.buildId : null
	} catch {
		return null
	}
}

let latestCheckId = 0

//	An unknown deployed id leaves the previous result in place, so a transient network error
//	neither raises nor clears the prompt. Checks can overlap (visibilitychange and focus often fire
//	together), so a check that was overtaken by a later one discards its result.
export async function checkForAppUpdate(fetchFn: typeof fetch = fetch): Promise<void> {
	const checkId = ++latestCheckId
	const deployedBuildId = await fetchDeployedBuildId(fetchFn)
	if (checkId !== latestCheckId) return
	if (deployedBuildId === null) return
	useAppUpdateStore.getState().setAvailableBuildId(deployedBuildId === currentBuildId ? null : deployedBuildId)
}

//	Checks on mount, every CHECK_INTERVAL_MS while the page is visible, and whenever the page
//	becomes visible or focused again.
export function useAppUpdateCheck() {
	useEffect(() => {
		const checkIfVisible = () => {
			if (document.visibilityState === 'visible')
				checkForAppUpdate() /*noawait*/
		}

		document.addEventListener('visibilitychange', checkIfVisible)
		window.addEventListener('focus', checkIfVisible)
		const interval = setInterval(checkIfVisible, CHECK_INTERVAL_MS)
		checkIfVisible()

		return () => {
			document.removeEventListener('visibilitychange', checkIfVisible)
			window.removeEventListener('focus', checkIfVisible)
			clearInterval(interval)
		}
	}, [])
}
